import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import {
  ArrowRight,
  Construction,
  Wrench,
  ShieldCheck,
  Truck,
  Menu,
  X,
  ArrowUp,
  Mail,
  Phone,
  MapPin,
  Download,
  Play,
  Accessibility,
  RotateCcw,
  Type,
  AlignLeft,
  Baseline,
  Contrast,
  ImageOff,
  Pause,
  BookOpenCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { getImageUrl } from '@/utils/imageUtils';
import { breadcrumbSchema, serviceSchema, useSeo } from '@/utils/seo';

const Button = ({ className = '', size, variant, type = 'button', ...props }) => {
  const sizeClass = size === 'icon' ? 'h-9 w-9' : size === 'lg' ? 'h-10 px-8' : 'h-9 px-4 py-2';
  const variantClass = variant === 'outline'
    ? 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground'
    : variant === 'ghost'
      ? 'hover:bg-accent hover:text-accent-foreground'
      : 'bg-primary text-primary-foreground shadow hover:bg-primary/90';

  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ${sizeClass} ${variantClass} ${className}`}
      {...props}
    />
  );
};

const Card = ({ className = '', ...props }) => (
  <div className={`rounded-xl border bg-card text-card-foreground shadow ${className}`} {...props} />
);

const CardContent = ({ className = '', ...props }) => (
  <div className={`p-6 pt-0 ${className}`} {...props} />
);

const Input = ({ className = '', type = 'text', ...props }) => (
  <input
    type={type}
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${className}`}
    {...props}
  />
);

const Textarea = ({ className = '', ...props }) => (
  <textarea
    className={`flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${className}`}
    {...props}
  />
);

const BitvOptionButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-center gap-4 sm:gap-5 p-4 sm:p-5 border rounded-3xl min-h-[88px] text-left transition-colors ${
      active
        ? 'bg-green-50 border-green-500 text-slate-950 shadow-sm'
        : 'bg-slate-100/80 hover:bg-white border-slate-200 text-slate-900'
    }`}
  >
    <span className={`flex flex-shrink-0 items-center justify-center rounded-full w-12 h-12 ${
      active ? 'bg-green-600 text-white' : 'bg-white text-slate-900'
    }`}>
      <Icon className="w-6 h-6" aria-hidden="true" />
    </span>
    <span className="font-bold text-base sm:text-lg leading-tight">{label}</span>
  </button>
);

export default function Landing() {
  const navigate = useNavigate();
  const publicContactEmail = 'info@spreewal-lift.de';
  const contactRecipientEmail = 'casianus@me.com';
  const minContactSubmitTimeMs = 2500;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const [bitvWidgetOpen, setBitvWidgetOpen] = useState(false);
  const [bitvOptions, setBitvOptions] = useState({
    largeText: false,
    lineHeight: false,
    textAlign: false,
    readableFont: false,
    highContrast: false,
    grayscale: false,
    hideImages: false,
    pauseAnimations: false
  });
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactFormStartedAt] = useState(Date.now());
  const [siteContent] = useState({});
  const platformData = null;
  const [dynamicStyles, setDynamicStyles] = useState('');

  const handleMobileNavClick = (event, targetId) => {
    event.preventDefault();
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
    // Menü mit einer leichten Verzögerung schließen, um den Scroll-Start zu ermöglichen
    setTimeout(() => {
      setMobileMenuOpen(false);
    }, 300);
  };
  
  // Smooth Animation Variants
  const fadeIn = (delay = 0) => ({
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.98
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.6, 
        delay, 
        ease: [0.23, 1, 0.32, 1]
      },
    },
  });

  const slideInLeft = (delay = 0) => ({
    hidden: { 
      opacity: 0, 
      x: -40,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { 
        duration: 0.6, 
        delay, 
        ease: [0.23, 1, 0.32, 1]
      },
    },
  });

  const slideInRight = (delay = 0) => ({
    hidden: { 
      opacity: 0, 
      x: 40,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { 
        duration: 0.6, 
        delay, 
        ease: [0.23, 1, 0.32, 1]
      },
    },
  });

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.05,
        ease: [0.25, 0.46, 0.45, 0.94]
      },
    },
  };

  const floatingAnimation = {
    hidden: { 
      opacity: 0, 
      y: 15,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.5, 
        ease: [0.23, 1, 0.32, 1]
      },
    },
    hover: {
      y: -3,
      scale: 1.02,
      transition: { 
        duration: 0.2, 
        ease: [0.23, 1, 0.32, 1] 
      },
    }
  };

  useEffect(() => {
    applyDynamicStyles({});
    
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 50;
      setNavScrolled(scrolled);
      setNavVisible(currentScrollY < 90 || currentScrollY < lastScrollY);
      
      if (currentScrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }

      lastScrollY = currentScrollY;
    };

    setIsAdminLoggedIn(Boolean(localStorage.getItem('auth_token')));
    const handleStorage = () => {
      setIsAdminLoggedIn(Boolean(localStorage.getItem('auth_token')));
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('admin-auth-changed', handleStorage);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('admin-auth-changed', handleStorage);
    };
  }, []);

  useEffect(() => {
    const classMap = {
      largeText: 'bitv-large-text',
      lineHeight: 'bitv-line-height',
      textAlign: 'bitv-text-left',
      readableFont: 'bitv-readable-font',
      highContrast: 'bitv-high-contrast',
      grayscale: 'bitv-grayscale',
      hideImages: 'bitv-hide-images',
      pauseAnimations: 'bitv-pause-animations'
    };
    const body = document.body;

    Object.entries(classMap).forEach(([key, className]) => {
      body.classList.toggle(className, Boolean(bitvOptions[key]));
    });

    return () => {
      Object.values(classMap).forEach((className) => body.classList.remove(className));
    };
  }, [bitvOptions]);

  const toggleBitvOption = (option) => {
    setBitvOptions((previousOptions) => ({
      ...previousOptions,
      [option]: !previousOptions[option]
    }));
  };

  const resetBitvOptions = () => {
    setBitvOptions({
      largeText: false,
      lineHeight: false,
      textAlign: false,
      readableFont: false,
      highContrast: false,
      grayscale: false,
      hideImages: false,
      pauseAnimations: false
    });
  };

  const applyDynamicStyles = (contentMap) => {
    const theme = contentMap.theme || {};
    const primaryColor = theme.primary_color?.content || '#15803d';
    const secondaryColor = theme.secondary_color?.content || '#047857';
    const accentColor = theme.accent_color?.content || '#166534';
    const backgroundColor = theme.background_color?.content || '#f8fafc';
    const textColor = theme.text_color?.content || '#1e293b';
    
    const css = `
      :root {
        --primary-color: ${primaryColor};
        --secondary-color: ${secondaryColor};
        --accent-color: ${accentColor};
        --background-color: ${backgroundColor};
        --text-color: ${textColor};
      }
      .bg-primary { background-color: var(--primary-color) !important; }
      .text-primary { color: var(--primary-color) !important; }
      .border-primary { border-color: var(--primary-color) !important; }
      .from-primary { --tw-gradient-from: var(--primary-color) !important; }
      .to-primary { --tw-gradient-to: var(--secondary-color) !important; }
      .bg-gradient-to-r.from-green-600.to-emerald-600 { 
        background: linear-gradient(to right, var(--primary-color), var(--secondary-color)) !important; 
      }
      .bg-green-600 { background-color: var(--primary-color) !important; }
      .hover\\:bg-green-700:hover { background-color: var(--accent-color) !important; }
      .text-green-700 { color: var(--accent-color) !important; }
      .text-green-600 { color: var(--primary-color) !important; }
    `;
    
    setDynamicStyles(css);
  };

  const getContent = (section, key, field = 'content', fallback = '') => {
    const value = siteContent[section]?.[key]?.[field] || fallback;
    return value;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const elapsedTime = Date.now() - contactFormStartedAt;
    if (contactForm.website || elapsedTime < minContactSubmitTimeMs) {
      setIsSubmitting(false);
      return;
    }

    const recipient = contactRecipientEmail;
    const subject = encodeURIComponent(`Anfrage Spreewald Lift${contactForm.name ? ` - ${contactForm.name}` : ''}`);
    const body = encodeURIComponent([
      'Neue Anfrage über die Website:',
      '',
      `Name: ${contactForm.name}`,
      `E-Mail: ${contactForm.email}`,
      `Telefon: ${contactForm.phone || '-'}`,
      `Angezeigte Kontaktadresse: ${publicContactEmail}`,
      '',
      'Nachricht:',
      contactForm.message || '-'
    ].join('\n'));

    window.location.href = `mailto:${recipient}?subject=${subject}&body=${body}`;

    setContactForm({ name: '', email: '', phone: '', message: '', website: '' });
    setIsSubmitting(false);
  };

  const handleInputChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  // Dynamic benefits from database
  const dynamicBenefits = [
    {
      icon: <Wrench className="w-8 h-8 text-slate-900" />,
      title: getContent('benefits', 'benefit_1_title', 'content', 'Moderne Technik'),
      description: getContent('benefits', 'benefit_1_description', 'content', 'Unsere Octoplus 21 Gelenkteleskopbühne bietet modernste Technologie für maximale Sicherheit und Effizienz.'),
    },
    {
      icon: <Truck className="w-8 h-8 text-slate-900" />,
      title: getContent('benefits', 'benefit_2_title', 'content', 'Zuverlässiger Service'),
      description: getContent('benefits', 'benefit_2_description', 'content', 'Professionelle Beratung und schnelle Verfügbarkeit für Ihre Projekte im Spreewald und Umgebung.'),
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-slate-900" />,
      title: getContent('benefits', 'benefit_3_title', 'content', 'Flexible Mietdauer'),
      description: getContent('benefits', 'benefit_3_description', 'content', 'Von Stunden bis Wochen – wir passen uns Ihren Projektanforderungen an.'),
    },
  ];

  // Determine logo and logo type with improved logic
  const logoType = getContent('hero', 'logo_type', 'content', 'image');
  const logoUrl = getContent('hero', 'company_logo', 'image_url', '/images/logos/spreewald-logo.svg');
  const companyName = getContent('hero', 'main_title', 'value', getContent('hero', 'main_title', 'content', 'Spreewald Lift'));

  const benefitsTitle = getContent('benefits', 'section_title', 'title', 'Warum Spreewald Lift?');
  const benefitsTitleParts = benefitsTitle.split('Spreewald Lift');
  const heroBrandTitle = getContent('hero', 'main_title', 'value', getContent('hero', 'main_title', 'title', 'Spreewald Lift'));
  const heroClaimTitle = getContent('hero', 'main_title', 'content', 'Ihre Höhe, unser Service');
  const heroImagePath = getContent('hero', 'hero_image', 'image_url', '/images/hero/product.png');
  const usesDefaultHeroImage = heroImagePath === '/images/hero/product.png';
  const machineTitle = getContent('machine', 'section_title', 'title', 'Unsere Octoplus 21');
  const machineTitleParts = machineTitle.split('Octoplus 21');
  const datasheetUrl = getContent('machine', 'datasheet_pdf', 'image_url');
  const bitvMode = getContent('bitv', 'bitv_mode', 'content', 'enhanced');
  const isBitvEnhanced = bitvMode === 'enhanced';
  const partners = [
    {
      name: 'Spreewald Grün',
      url: 'https://spreewald-gruen.de',
      logo: '/images/logos/partners/spreewald-gruen.svg'
    },
    {
      name: 'MOBIL-WC',
      url: 'https://wc-toilette.de',
      logo: '/images/logos/partners/mobil-wc.svg'
    },
    {
      name: 'pixelcoda',
      url: 'https://pixelcoda.de',
      logo: 'https://pixelcoda.de/assets/images/43bd873b1_logo-png-09fef67a.png'
    }
  ];

  const seoTitle = getContent('seo', 'seo_title', 'content', 'Spreewald Lift | Arbeitsbühne mieten im Spreewald');
  const seoDescription = getContent('seo', 'seo_description', 'content', 'Arbeitsbühne mieten im Spreewald: moderne Hebebühne für Baumpflege, Fassadenarbeiten, Dacharbeiten und Montagearbeiten in Lübbenau und Umgebung.');
  const seoRobots = getContent('seo', 'seo_robots', 'content', 'index, follow, max-image-preview:large');
  const canonicalUrl = getContent('seo', 'canonical_url', 'content', 'https://spreewald-lift.de/');
  const ogImageRaw = getContent('seo', 'og_image', 'image_url', '/images/hero/product.png');

  useSeo({
    title: seoTitle,
    description: seoDescription,
    path: canonicalUrl,
    image: ogImageRaw,
    robots: seoRobots,
    jsonLd: [
      serviceSchema({
        name: 'Arbeitsbühne mieten im Spreewald',
        description: seoDescription,
        path: '/'
      }),
      breadcrumbSchema([{ name: 'Startseite', path: '/' }])
    ]
  });

  return (
    <div className="bg-[#f7f8f5] overflow-hidden text-slate-950">
      {/* Skip Link for Screen Readers */}
      <a href="#main-content" className="skip-link">
        Zum Hauptinhalt springen
      </a>
      
      {/* Dynamic Styles */}
      <style dangerouslySetInnerHTML={{ __html: dynamicStyles }} />
      
      {/* Premium Global Shadow Styles - Soft & Light */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* Global Smooth Scroll */
        html {
          scroll-behavior: smooth !important;
          scroll-padding-top: 80px; /* Account for fixed header */
        }
        
        /* Enhanced Smooth Scroll for all anchor links */
        a[href^="#"] {
          scroll-behavior: smooth !important;
        }
        
        /* Respect user preference for reduced motion */
        @media (prefers-reduced-motion: reduce) {
          html {
            scroll-behavior: auto !important;
          }
        }
        
        /* Enhanced Accessibility Features */
        /* Focus indicators for keyboard navigation */
        *:focus-visible {
          outline: 2px solid #22c55e !important;
          outline-offset: 2px !important;
        }
        
        /* Skip link for screen readers */
        .skip-link {
          position: absolute;
          top: -40px;
          left: 6px;
          background: #22c55e;
          color: white;
          padding: 8px;
          text-decoration: none;
          border-radius: 4px;
          z-index: 1000;
        }
        
        .skip-link:focus {
          top: 6px;
        }

        body.bitv-large-text {
          font-size: 112%;
        }

        body.bitv-line-height p,
        body.bitv-line-height li,
        body.bitv-line-height a,
        body.bitv-line-height button {
          line-height: 1.8 !important;
        }

        body.bitv-text-left h1,
        body.bitv-text-left h2,
        body.bitv-text-left h3,
        body.bitv-text-left p,
        body.bitv-text-left li {
          text-align: left !important;
        }

        body.bitv-readable-font * {
          font-family: Arial, Helvetica, sans-serif !important;
          letter-spacing: 0 !important;
        }

        body.bitv-high-contrast,
        body.bitv-high-contrast main,
        body.bitv-high-contrast section {
          background: #ffffff !important;
          color: #020617 !important;
        }

        body.bitv-high-contrast a,
        body.bitv-high-contrast button {
          text-decoration-thickness: 2px;
        }

        body.bitv-grayscale {
          filter: grayscale(1);
        }

        body.bitv-hide-images img,
        body.bitv-hide-images picture,
        body.bitv-hide-images video {
          visibility: hidden !important;
        }

        body.bitv-pause-animations *,
        body.bitv-pause-animations *::before,
        body.bitv-pause-animations *::after {
          animation: none !important;
          transition: none !important;
          scroll-behavior: auto !important;
        }
        
        /* Optimized Shadow System - Static, no transitions */
        .shadow-sm { 
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03), 0 1px 3px rgba(0, 0, 0, 0.02) !important; 
        }
        .shadow { 
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.03) !important; 
        }
        .shadow-md { 
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05), 0 4px 10px rgba(0, 0, 0, 0.04) !important; 
        }
        .shadow-lg { 
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.06), 0 6px 16px rgba(0, 0, 0, 0.05) !important; 
        }
        .shadow-xl { 
          box-shadow: 0 20px 48px rgba(0, 0, 0, 0.07), 0 10px 24px rgba(0, 0, 0, 0.06) !important; 
        }
        
        /* Performance optimizations */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        /* Remove will-change from elements to improve performance */
        .no-will-change {
          will-change: auto !important;
        }
      ` }} />
      
      {/* Custom CSS from Admin */}
      {getContent('custom_styles', 'global_css', 'content') && (
        <style dangerouslySetInnerHTML={{ __html: getContent('custom_styles', 'global_css', 'content') }} />
      )}

      {isBitvEnhanced && (
        <>
          <a
            href="#main-content"
            className="top-3 left-3 z-[100] fixed bg-white px-4 py-2 rounded-full font-semibold text-slate-950 -translate-y-24 focus:translate-y-0 transition-transform"
          >
            {getContent('bitv', 'skip_link_text', 'content', 'Zum Inhalt springen')}
          </a>
          <style dangerouslySetInnerHTML={{ __html: `
            :focus-visible {
              outline: 3px solid #16a34a !important;
              outline-offset: 4px !important;
            }
            @media (prefers-reduced-motion: reduce) {
              *, *::before, *::after {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                scroll-behavior: auto !important;
                transition-duration: 0.01ms !important;
              }
            }
          ` }} />
        </>
      )}

      {/* Premium Navigation with Scroll Animation */}
      <motion.header 
        className={`fixed top-3 sm:top-4 left-0 right-0 z-[60] w-full transition-all duration-300 ${
          navScrolled 
            ? 'py-3' 
            : 'py-4'
        }`}
        initial={{ y: -100 }}
        animate={{
          y: navVisible ? 0 : -110,
          opacity: navVisible ? 1 : 0,
          pointerEvents: navVisible ? 'auto' : 'none'
        }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{ touchAction: 'manipulation', isolation: 'isolate', paddingTop: 'env(safe-area-inset-top)' }}
        role="banner"
        aria-label="Hauptnavigation"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div 
            className="flex justify-between items-center bg-white shadow-[0_18px_60px_rgba(15,23,42,0.16)] px-3 sm:px-5 lg:px-6 border border-slate-200/80 rounded-[1.75rem] sm:rounded-full h-14 sm:h-16 transition-all duration-300"
            layout
          >
            <motion.div layout style={{ touchAction: 'manipulation' }}>
              <Link to={createPageUrl('Landing')} className="z-10 relative flex items-center gap-2 sm:gap-3 pr-2 min-w-0">
                {logoType === 'image' && logoUrl ? (
                  <>
                    <motion.div 
                      className="flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-sm p-2 rounded-full w-9 sm:w-10 h-9 sm:h-10"
                      whileHover={{ scale: 1.05, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <img 
                        src={getImageUrl(logoUrl)} 
                        alt="Firmenlogo" 
                        width="40"
                        height="40"
                        decoding="async"
                        className="brightness-0 invert w-full h-full object-contain"
                        onError={(e) => {
                          console.error("Logo konnte nicht geladen werden:", logoUrl);
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                      />
                    </motion.div>
                    <motion.h1 
                      className={`font-bold transition-all duration-300 ${
                        navScrolled 
                          ? 'text-slate-950 text-sm sm:text-base' 
                          : 'text-slate-950 text-base sm:text-lg'
                      }`}
                      layout
                    >
                      <span className="block max-w-[190px] sm:max-w-none truncate">{companyName}</span>
                    </motion.h1>
                  </>
                ) : logoType === 'text' ? (
                  <motion.span 
                      className={`font-bold transition-all duration-300 ${
                        navScrolled 
                          ? 'text-slate-950 text-sm sm:text-base' 
                          : 'text-slate-950 text-base sm:text-lg'
                    }`}
                    layout
                  >
                    <span className="block max-w-[190px] sm:max-w-none truncate">{companyName}</span>
                  </motion.span>
                ) : (
                  <>
                    <motion.div 
                      className="flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-sm rounded-full w-9 sm:w-10 h-9 sm:h-10"
                      whileHover={{ scale: 1.05, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Construction className="w-4 sm:w-6 h-4 sm:h-6 text-white" />
                    </motion.div>
                    <motion.h1 
                      className={`font-bold transition-all duration-300 ${
                        navScrolled 
                          ? 'text-slate-950 text-sm sm:text-base' 
                          : 'text-slate-950 text-base sm:text-lg'
                      }`}
                      layout
                    >
                      <span className="block max-w-[190px] sm:max-w-none truncate">{companyName}</span>
                    </motion.h1>
                  </>
                )}
              </Link>
            </motion.div>
            
            {/* Desktop Navigation with Premium Icons */}
            <motion.nav 
              className="hidden md:flex items-center gap-2 lg:gap-4"
              layout
              role="navigation"
              aria-label="Hauptmenü"
            >
              <motion.a 
                href="#vorteile" 
                className="group flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full font-semibold text-slate-700 hover:bg-slate-950/[0.04] hover:text-slate-950 text-sm transition-colors"
                whileHover={{ y: -2, color: "#22c55e" }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="flex justify-center items-center bg-transparent rounded-full w-5 h-5"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    whileHover={{ color: "#22c55e" }}
                    transition={{ duration: 0.2 }}
                  >
                    <ShieldCheck className="w-4 h-4 text-green-600" />
                  </motion.div>
                </motion.div>
                <span>Vorteile</span>
              </motion.a>
              <motion.a 
                href="#maschine" 
                className="group flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full font-semibold text-slate-700 hover:bg-slate-950/[0.04] hover:text-slate-950 text-sm transition-colors"
                whileHover={{ y: -2, color: "#22c55e" }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="flex justify-center items-center bg-transparent rounded-full w-5 h-5"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    whileHover={{ color: "#22c55e" }}
                    transition={{ duration: 0.2 }}
                  >
                    <Truck className="w-4 h-4 text-green-600" />
                  </motion.div>
                </motion.div>
                <span>Unsere Maschine</span>
              </motion.a>
              <motion.div
                whileHover={{ y: -2, color: "#22c55e" }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to="/blog"
                  className="group flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full font-semibold text-slate-700 hover:bg-slate-950/[0.04] hover:text-slate-950 text-sm transition-colors"
                >
                  <motion.div
                    className="flex justify-center items-center bg-transparent rounded-full w-5 h-5"
                    whileHover={{
                      scale: 1.1,
                      rotate: 5
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <BookOpenCheck className="w-4 h-4 text-green-600" />
                  </motion.div>
                  <span>Blog</span>
                </Link>
              </motion.div>
              <motion.a 
                href="#kontakt" 
                className="group flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full font-semibold text-slate-700 hover:bg-slate-950/[0.04] hover:text-slate-950 text-sm transition-colors"
                whileHover={{ y: -2, color: "#22c55e" }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="flex justify-center items-center bg-transparent rounded-full w-5 h-5"
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    whileHover={{ color: "#22c55e" }}
                    transition={{ duration: 0.2 }}
                  >
                    <Mail className="w-4 h-4 text-green-600" />
                  </motion.div>
                </motion.div>
                <span>Kontakt</span>
              </motion.a>
              {isAdminLoggedIn && (
                <Link to={createPageUrl('AdminDashboard')}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      variant="ghost" 
                      className="group flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-slate-600 hover:bg-slate-950/[0.04] hover:text-slate-950 text-sm"
                    >
                      <motion.div
                        className="flex justify-center items-center bg-transparent rounded-full w-4 h-4"
                        whileHover={{ 
                          scale: 1.1, 
                          rotate: 5
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ color: "#475569" }}
                          transition={{ duration: 0.2 }}
                        >
                          <Wrench className="w-3 h-3 text-slate-500" />
                        </motion.div>
                      </motion.div>
                      <span>Admin</span>
                    </Button>
                  </motion.div>
                </Link>
              )}
              <Link to={createPageUrl(getContent('hero', 'cta_button', 'button_url', 'Booking'))}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button 
                    className={`group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg text-white font-semibold rounded-full ${
                      navScrolled 
                        ? 'px-4 py-2 text-sm' 
                        : 'px-5 py-2.5 text-sm'
                    }`}
                  >
                    {navScrolled ? 'Anfragen' : getContent('hero', 'cta_button', 'button_text', 'Anfragen')}
                    <motion.div
                      className="ml-2"
                      whileHover={{ x: 3 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </motion.div>
                  </Button>
                </motion.div>
              </Link>
            </motion.nav>

            {/* Mobile Menu Button */}
            <motion.button 
              className={`mobile-menu-button md:hidden flex flex-shrink-0 items-center justify-center bg-white/70 p-2 rounded-full relative z-[60] ${
                navScrolled 
                  ? 'text-slate-700' 
                  : 'text-slate-600'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Navigation schließen' : 'Navigation öffnen'}
              aria-expanded={mobileMenuOpen}
              whileHover={{ 
                scale: 1.05,
                backgroundColor: navScrolled ? "#f1f5f9" : "#f9fafb"
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
              style={{ touchAction: 'manipulation', minHeight: '44px', minWidth: '44px' }}
            >
              <motion.div
                animate={{ rotate: mobileMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
      </motion.header>

      {/* Premium Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="z-[70] fixed inset-0 bg-slate-950/35 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Premium Mobile Menu Panel */}
            <motion.div 
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ 
                type: 'spring', 
                damping: 25, 
                stiffness: 200,
                opacity: { duration: 0.3 }
              }}
              className="inset-y-0 right-0 z-[80] fixed bg-white/95 shadow-[0_30px_90px_rgba(15,23,42,0.22)] backdrop-blur-2xl border-white/80 border-l w-[min(24rem,calc(100vw-1rem))] overflow-hidden"
              style={{ touchAction: 'manipulation' }}
            >
              {/* Premium Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 opacity-80"></div>
              
              {              /* Close Button */}
              <motion.button
                className="right-4 z-20 absolute flex justify-center items-center bg-white/90 hover:bg-white shadow-sm border border-slate-200/70 rounded-full w-10 h-10 transition-all duration-300"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Navigation schließen"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                style={{ touchAction: 'manipulation', minHeight: '44px', minWidth: '44px', top: 'max(1rem, calc(env(safe-area-inset-top) + 1rem))' }}
              >
                <X className="w-5 h-5 text-slate-600" />
              </motion.button>
              
              <div
                className="relative z-10 flex flex-col px-5 sm:px-6 h-full overflow-y-auto"
                style={{
                  paddingTop: 'max(5rem, calc(env(safe-area-inset-top) + 4rem))',
                  paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 1rem))'
                }}
              >
                {/* Premium Header */}
                <motion.div 
                  className="flex items-center space-x-3 mb-6"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {logoType === 'image' && logoUrl ? (
                    <>
                      <div className="flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-sm p-1.5 rounded-full w-10 h-10">
                        <img 
                        src={getImageUrl(logoUrl)} 
                        alt="Firmenlogo" 
                        width="40"
                        height="40"
                        decoding="async"
                        className="brightness-0 invert w-full h-full object-contain"
                          onError={(e) => {
                            console.error("Canvas Logo konnte nicht geladen werden:", logoUrl);
                            // Fallback to Construction icon
                            e.target.style.display = 'none';
                            const constructionIcon = document.createElement('div');
                            constructionIcon.innerHTML = '<svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/></svg>';
                            e.target.parentElement.appendChild(constructionIcon.firstChild);
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg leading-tight">{companyName}</h3>
                        <p className="text-slate-500 text-xs">Navigation</p>
                      </div>
                    </>
                  ) : logoType === 'text' ? (
                    <>
                      <div className="flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-sm rounded-full w-10 h-10">
                        <span className="font-bold text-white text-base">{companyName.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg leading-tight">{companyName}</h3>
                        <p className="text-slate-500 text-xs">Navigation</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-sm rounded-full w-10 h-10">
                        <Construction className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg leading-tight">{companyName}</h3>
                        <p className="text-slate-500 text-xs">Navigation</p>
                      </div>
                    </>
                  )}
                </motion.div>

                {/* Premium Menu Items */}
                <motion.div 
                  className="flex flex-col flex-1 space-y-2 min-h-0"
                  initial="hidden"
                  animate="visible"
                  variants={staggerContainer}
                >
                  <motion.div variants={fadeIn(0)}>
                    <Link 
                      to={createPageUrl('Landing')}
                      className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <motion.div 
                        className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Construction className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="group-hover:text-green-700 transition-colors duration-300">Home</span>
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                    </Link>
                  </motion.div>
                  
                  <motion.div variants={fadeIn(0.1)}>
                    <a 
                      href="#vorteile" 
                      className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300" 
                      onClick={(e) => handleMobileNavClick(e, '#vorteile')}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <motion.div 
                        className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <ShieldCheck className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="group-hover:text-green-700 transition-colors duration-300">Vorteile</span>
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                    </a>
                  </motion.div>
                  
                  <motion.div variants={fadeIn(0.2)}>
                    <a 
                      href="#maschine" 
                      className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300" 
                      onClick={(e) => handleMobileNavClick(e, '#maschine')}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <motion.div 
                        className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Truck className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="group-hover:text-green-700 transition-colors duration-300">Unsere Maschine</span>
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                    </a>
                  </motion.div>
                  
                  <motion.div variants={fadeIn(0.3)}>
                    <Link 
                      to="/blog"
                      className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300"
                      onClick={() => setMobileMenuOpen(false)}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <motion.div 
                        className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <BookOpenCheck className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="group-hover:text-green-700 transition-colors duration-300">Blog</span>
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                    </Link>
                  </motion.div>
                  
                  <motion.div variants={fadeIn(0.4)}>
                    <a 
                      href="#kontakt" 
                      className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300" 
                      onClick={(e) => handleMobileNavClick(e, '#kontakt')}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <motion.div 
                        className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Phone className="w-5 sm:w-6 h-5 sm:h-6 text-slate-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="group-hover:text-green-700 transition-colors duration-300">Kontakt</span>
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" />
                      </motion.div>
                    </a>
                  </motion.div>
                  
                  {isAdminLoggedIn && (
                    <motion.div variants={fadeIn(0.5)}>
                      <Link 
                        to={createPageUrl('AdminDashboard')}
                        className="group z-10 relative flex items-center space-x-3 hover:bg-white/80 px-3 py-3 rounded-2xl font-medium text-slate-700 text-base transition-all duration-300"
                        onClick={() => setMobileMenuOpen(false)}
                        style={{ touchAction: 'manipulation' }}
                      >
                        <motion.div 
                          className="flex flex-shrink-0 justify-center items-center bg-white group-hover:bg-slate-950 rounded-full shadow-sm border border-slate-200/70 w-11 h-11 transition-all duration-300"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <Wrench className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors duration-300" />
                        </motion.div>
                        <span className="group-hover:text-slate-800 transition-colors duration-300">Admin</span>
                        <motion.div
                          className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                          initial={{ x: -10 }}
                          whileHover={{ x: 0 }}
                        >
                          <ArrowRight className="w-4 h-4 text-slate-500" />
                        </motion.div>
                      </Link>
                    </motion.div>
                  )}
                </motion.div>
                
                {/* Premium CTA Button */}
                <motion.div 
                  className="shrink-0 mt-auto pt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Link to={createPageUrl(getContent('hero', 'cta_button', 'button_url', 'Booking'))} onClick={() => setMobileMenuOpen(false)} className="z-10 relative" style={{ touchAction: 'manipulation' }}>
                    <motion.div
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="group"
                    >
                      <Button className="relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl px-4 py-3 rounded-full w-full min-h-[52px] overflow-hidden font-semibold text-white text-base sm:text-lg transition-all duration-300">
                        <motion.div 
                          className="flex justify-center items-center min-w-0"
                          whileHover={{ x: 2 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Construction className="flex-shrink-0 mr-2.5 w-5 h-5" />
                          <span className="min-w-0 truncate">{getContent('hero', 'cta_button', 'button_text', 'Jetzt anfragen')}</span>
                          <motion.div
                            className="flex-shrink-0 ml-2"
                            animate={{ x: [0, 3, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <ArrowRight className="w-5 h-5" />
                          </motion.div>
                        </motion.div>
                        
                        {/* Premium Shine Effect */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        />
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Hero Banner */}
      <main 
        id="main-content"
        className="wp-block-elevation-home-banner z-0 relative flex flex-col items-center bg-[#f7f8f5] pt-28 sm:pt-32 lg:pt-24 min-h-[720px] sm:min-h-[760px] overflow-hidden home-banner alignfull"
        role="main"
        aria-label="Hauptinhalt"
        onMouseMove={(e) => {
          // Optimierter Parallax-Effekt - sanfter und ohne Blitzen
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const deltaX = (x - centerX) / centerX;
          const deltaY = (y - centerY) / centerY;
          
          // Sanfter Parallax für Hintergrundbild
          const bgImage = e.currentTarget.querySelector('.home-banner__background img');
          if (bgImage) {
            const bgMoveX = deltaX * -8; // Reduzierte Bewegung
            const bgMoveY = deltaY * -5;
            bgImage.style.objectPosition = `calc(50% + ${bgMoveX}px) calc(50% + ${bgMoveY}px)`;
          }
          
          // Sanfter Parallax für Hero-Bild
          const heroImage = e.currentTarget.querySelector('.media-image');
          if (heroImage) {
            const heroMoveX = deltaX * -6; // Reduzierte Bewegung
            const heroMoveY = deltaY * -4;
            heroImage.style.transform = `translateX(${heroMoveX}px) translateY(${heroMoveY}px)`;
          }

          // Sanfter Parallax für Floating Cards
          const floatingCards = e.currentTarget.querySelectorAll('.floating-card');
          floatingCards.forEach((card, index) => {
            const cardMoveX = deltaX * (3 + index * 1); // Sehr sanfte Bewegung
            const cardMoveY = deltaY * (2 + index * 0.5);
            card.style.transform = `translateX(${cardMoveX}px) translateY(${cardMoveY}px)`;
          });
        }}
        onMouseLeave={(e) => {
          // Sanftes Zurücksetzen
          const bgImage = e.currentTarget.querySelector('.home-banner__background img');
          if (bgImage) {
            bgImage.style.objectPosition = '50% 50%';
          }
          
          const heroImage = e.currentTarget.querySelector('.media-image');
          if (heroImage) {
            heroImage.style.transform = 'translateX(0px) translateY(0px)';
          }

          // Floating Cards zurücksetzen
          const floatingCards = e.currentTarget.querySelectorAll('.floating-card');
          floatingCards.forEach((card) => {
            card.style.transform = 'translateX(0px) translateY(0px)';
          });
        }}
      >
        {/* Background Picture with Responsive Images */}
        <picture className="absolute inset-0 w-full h-full opacity-35 saturate-75 home-banner__background">
          <source 
            media="(max-width:575px)" 
            srcSet="/images/assets/bg-desktop-scaled.webp" 
          />
          <source 
            media="(max-width:1200px)" 
            srcSet="/images/assets/bg-desktop-scaled.webp" 
          />
          <img 
            decoding="async" 
            loading="lazy" 
            fetchPriority="low"
            src="/images/assets/bg-desktop-scaled.webp" 
            alt="" 
            width="1920" 
            height="1080" 
            className="w-full h-full object-cover home-banner__background" 
            style={{ 
              objectPosition: '50% 50%'
            }}
          />
        </picture>

        {/* Container with Content and Image */}
        <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl container home-banner__container">
          <div className="flex lg:flex-row flex-col lg:items-center gap-10 lg:gap-16 py-8 sm:py-16 lg:pt-24 lg:pb-12 h-auto min-h-full">
            
              {/* Content Section - Following  block structure */}
             <motion.div 
               className="wp-block-group wp-block-group-is-layout-flow flex-1 lg:flex-[0.42] max-w-full home-banner__content is-layout-flow"
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true, amount: 0.2 }}
               variants={staggerContainer}
             >
               <motion.h1 
                 className="wp-block-heading mb-6 sm:mb-8 font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.94] tracking-normal"
                 variants={slideInLeft(0)}
               >
                 <span className="text-slate-900">
                   {heroBrandTitle}
                 </span>{' '}
                 <mark className="has-inline-color bg-transparent font-semibold has-ui-foreground-primary-text-headings-span-color text-green-600">
                   {heroClaimTitle}
                 </mark>
                 <mark className="has-inline-color bg-transparent text-green-600 has-ui-foreground-primary-accent-01-color">.</mark>
               </motion.h1>

               <motion.p 
                 className="mb-8 sm:mb-10 max-w-xl font-normal text-slate-600 text-lg sm:text-xl leading-8"
                 variants={slideInLeft(0.2)}
               >
                 {getContent('hero', 'description', 'content', 'Mieten Sie unsere moderne Octoplus 21 Gelenkteleskopbühne – zuverlässig, sicher und perfekt für alle Ihre Höhenarbeiten im Spreewald und Umgebung.')}
               </motion.p>

               <motion.div 
                 className="wp-block-buttons-is-layout-flex wp-block-elevation-buttons lg:flex hidden is-layout-flex flex-wrap gap-4 buttons"
                 variants={slideInLeft(0.4)}
               >
                 <a href="#maschine">
                   <motion.div
                     whileHover={{ scale: 1.05, y: -2 }}
                     whileTap={{ scale: 0.98 }}
                     transition={{ duration: 0.2 }}
                   >
                     <Button 
                       className="wp-block-elevation-button fade-in-bottom bg-slate-950 hover:bg-slate-800 shadow-lg px-7 sm:px-9 py-3 sm:py-4 rounded-full font-semibold text-white text-base transition-all duration-300 animate button cta cta--primary-2"
                       aria-label="Mehr über unsere Octoplus 21 erfahren"
                     >
                       <Play className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                       Mehr erfahren
                     </Button>
                   </motion.div>
                 </a>
               </motion.div>
             </motion.div>

             {/* Media Section - Hero Image following  structure */}
             <motion.span 
               className="relative flex flex-shrink-0 lg:flex-[0.58] justify-center lg:justify-end w-full lg:w-auto media"
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true, amount: 0.2 }}
               variants={staggerContainer}
             >
               <motion.picture variants={slideInRight(0)} className="contents">
                 {usesDefaultHeroImage && (
                   <source
                     srcSet="/images/hero/product-640.webp 640w, /images/hero/product.webp 1094w"
                     sizes="(min-width: 1280px) 52rem, (min-width: 1024px) 58vw, 100vw"
                     type="image/webp"
                   />
                 )}
                 <img
                   width="1094"
                   height="678"
                   src={getImageUrl(heroImagePath)}
                   sizes="(min-width: 1280px) 52rem, (min-width: 1024px) 58vw, 100vw"
                   className="drop-shadow-[0_36px_60px_rgba(15,23,42,0.18)] w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-5xl h-auto object-contain media-image"
                   alt={getContent('hero', 'hero_image', 'alt_text', 'Spreewald Lift - Professionelle Arbeitsbühnen-Vermietung')}
                   decoding="async"
                   loading="eager"
                   fetchPriority="high"
                   title="Spreewald Lift - Professionelle Arbeitsbühnen-Vermietung"
                   onError={(e) => {
                     e.currentTarget.onerror = null;
                     e.currentTarget.src = '/images/hero/product.png';
                   }}
                 />
               </motion.picture>

               {/* Floating Stats Cards - 3 Cards mit spezifischen Inhalten */}
               <motion.div 
                 className="top-4 sm:top-8 left-4 sm:left-8 z-20 floating-card absolute bg-white/80 shadow-xl backdrop-blur-2xl px-3 sm:px-4 py-2 sm:py-3 border border-white/80 rounded-2xl"
                 variants={floatingAnimation}
                 initial="hidden"
                 whileInView="visible"
                 viewport={{ once: true }}
                 whileHover={{ scale: 1.05, y: -2 }}
                 transition={{ duration: 0.2 }}
               >
                 <div className="flex items-center space-x-2 sm:space-x-3">
                   <motion.div 
                     className="bg-green-100 p-1.5 sm:p-2 rounded-lg"
                     whileHover={{ rotate: 10, scale: 1.05 }}
                     transition={{ duration: 0.2 }}
                   >
                     <Truck className="w-3 sm:w-4 lg:w-5 h-3 sm:h-4 lg:h-5 text-green-600" />
                   </motion.div>
                   <div>
                     <motion.p 
                       className="font-bold text-slate-900 text-xs sm:text-sm lg:text-base"
                       whileHover={{ scale: 1.05 }}
                       transition={{ duration: 0.2 }}
                     >
                       21m
                     </motion.p>
                     <p className="text-slate-500 text-xs sm:text-sm">Arbeitshöhe</p>
                   </div>
                 </div>
               </motion.div>

               <motion.div 
                 className="top-20 sm:top-32 right-4 sm:right-8 z-20 floating-card absolute bg-white/80 shadow-xl backdrop-blur-2xl px-3 sm:px-4 py-2 sm:py-3 border border-white/80 rounded-2xl"
                 variants={floatingAnimation}
                 initial="hidden"
                 whileInView="visible"
                 viewport={{ once: true }}
                 whileHover={{ scale: 1.05, y: -2 }}
                 transition={{ duration: 0.2 }}
               >
                 <div className="flex items-center space-x-2 sm:space-x-3">
                   <motion.div 
                     className="bg-emerald-100 p-1.5 sm:p-2 rounded-lg"
                     whileHover={{ rotate: 10, scale: 1.05 }}
                     transition={{ duration: 0.2 }}
                   >
                     <ShieldCheck className="w-3 sm:w-4 lg:w-5 h-3 sm:h-4 lg:h-5 text-emerald-600" />
                   </motion.div>
                   <div>
                     <motion.p 
                       className="font-bold text-slate-900 text-xs sm:text-sm lg:text-base"
                       whileHover={{ scale: 1.05 }}
                       transition={{ duration: 0.2 }}
                     >
                       24/7
                     </motion.p>
                     <p className="text-slate-500 text-xs sm:text-sm">Notfall-Service</p>
                   </div>
                 </div>
               </motion.div>


             </motion.span>

          </div>
          
          {/* Professionell Badge - centered at bottom */}
          <motion.div 
            className="hidden md:block bottom-10 left-1/2 z-20 floating-card absolute bg-white/80 shadow-xl backdrop-blur-2xl px-4 sm:px-6 py-3 sm:py-4 border border-white/80 rounded-2xl -translate-x-1/2 transform"
            variants={floatingAnimation}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05, y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center space-x-3">
              <motion.div 
                className="bg-blue-100 p-2.5 rounded-lg"
                whileHover={{ rotate: 10, scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Wrench className="w-5 h-5 text-blue-600" />
              </motion.div>
              <div>
                <motion.p 
                  className="font-bold text-slate-900 text-base"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  Professionell
                </motion.p>
                <p className="text-slate-500 text-sm">Einweisung</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="relative z-20 flex lg:hidden justify-center mt-6 pb-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeIn(0.1)}
          >
            <a href="#maschine">
              <motion.div
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button 
                  className="bg-slate-950 hover:bg-slate-800 shadow-lg px-7 py-4 rounded-full font-semibold text-white text-base transition-all duration-300"
                  aria-label="Mehr über unsere Octoplus 21 erfahren"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Mehr erfahren
                </Button>
              </motion.div>
            </a>
          </motion.div>
        </div>

          {           /* Optimized Styles - Entferne CSS-Animationen */}
          <style dangerouslySetInnerHTML={{ __html: `
           .home-banner__background {
              object-position: 50% 50% !important;
           }
           
           /* Mobile Touch Optimization */
           @media (max-width: 768px) {
             button {
               min-height: 44px !important;
               min-width: 44px !important;
               touch-action: manipulation !important;
             }
             
             .mobile-menu-button {
               position: relative !important;
               z-index: 60 !important;
               isolation: isolate !important;
             }
           }

           /* container */
           .container {
             width: 100%;
             max-width: 1200px;
             margin-left: auto;
             margin-right: auto;
             padding-left: 1rem;
             padding-right: 1rem;
           }

           /*  block styling */
           .wp-block-group-is-layout-flow > * + * {
             margin-block-start: 1.5rem;
           }

           .wp-block-buttons-is-layout-flex {
             align-items: center;
             gap: 0.5rem;
           }

           /* Enhanced mark styling */
           .has-inline-color {
             color: inherit;
           }

           .has-ui-foreground-primary-text-headings-span-color {
             color: var(--primary-color, #22c55e) !important;
           }

           .has-ui-foreground-primary-accent-01-color {
             color: var(--primary-color, #22c55e) !important;
           }

           /* Optimiertes Hero-Bild - Kein Blitzen */
           .media-image {
             opacity: 1 !important;
             transition: transform 0.15s ease-out !important;
           }

           .home-banner__background img {
             opacity: 1 !important;
             transition: object-position 0.15s ease-out !important;
           }

           /* Entferne Parallax auf Mobile für bessere Performance */
           @media (max-width: 768px) {
             .media-image {
               transform: none !important;
               transition: none !important;
             }
             .home-banner__background img {
               object-position: 50% 50% !important;
               transition: none !important;
             }
             .floating-card {
               transform: none !important;
               transition: none !important;
             }
           }

           /* Floating Cards Transitions */
           .floating-card {
             transition: transform 0.15s ease-out !important;
           }
          ` }} />
          
          {/* Schräges Polygon für unteren Abschluss */}
          <div className="hidden right-0 bottom-[-30px] left-0 relative w-full h-16 sm:h-32 lg:h-60 overflow-hidden">
            <img 
              src="/images/assets/bg-hero-new-bottom.svg"
              alt=""
              width="1440"
              height="240"
              loading="lazy"
              decoding="async"
              className="block w-full h-full object-cover"
              style={{ display: 'block' }}
            />
          </div>
      </main>

      {/* Benefits Section - Optimiert für Performance */}
      <section 
        id="vorteile" 
        className="relative py-20 sm:py-28"
        aria-labelledby="benefits-heading"
        role="region"
      >
        <div className="absolute inset-0 bg-[#f7f8f5]"></div>
        
        <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <motion.div 
            className="mb-12 sm:mb-16 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeIn()}
              id="benefits-heading"
              className="mb-4 sm:mb-6 font-semibold text-slate-950 text-3xl sm:text-4xl md:text-6xl tracking-normal"
            >
              {benefitsTitleParts[0]}<span className="text-green-600">Spreewald Lift</span>{benefitsTitleParts[1]}
            </motion.h2>
            <motion.p 
              variants={fadeIn(0.1)}
              className="mx-auto px-2 max-w-2xl text-slate-700 text-base sm:text-lg leading-8"
            >
              {getContent('benefits', 'section_title', 'content', 'Wir unterstützen Sie mit modernster Technik und exzellentem Service für einen reibungslosen Projektablauf.')}
            </motion.p>
          </motion.div>

          <motion.div 
            className="gap-5 sm:gap-6 grid grid-cols-1 md:grid-cols-3 items-stretch"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            {dynamicBenefits.map((benefit, index) => (
              <motion.div 
                key={index} 
                variants={fadeIn()}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="h-full"
              >
                <Card className="group relative bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)] hover:shadow-[0_34px_90px_rgba(15,23,42,0.13)] border border-slate-200/70 rounded-[2rem] h-full min-h-[340px] overflow-hidden text-left transition-all duration-300">
                  <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white"></div>
                  <div className="top-0 right-0 left-0 absolute bg-gradient-to-b from-emerald-50/80 to-transparent h-32"></div>
                  <div className="absolute -top-24 -right-24 bg-green-100/70 blur-3xl rounded-full w-52 h-52 transition-transform duration-500 group-hover:scale-125"></div>
                  <div className="right-8 bottom-8 absolute font-semibold text-[8rem] text-emerald-950/[0.035] group-hover:text-emerald-950/[0.075] leading-none select-none pointer-events-none transition-all duration-500 ease-out group-hover:-translate-x-2 group-hover:-translate-y-1 group-hover:scale-110">
                    0{index + 1}
                  </div>
                  <CardContent className="relative flex flex-col justify-between p-8 sm:p-9 h-full">
                    <div className="flex justify-center">
                      <motion.div 
                        className="inline-flex justify-center items-center bg-[linear-gradient(145deg,#ffffff_0%,#f8fafc_42%,#ecfdf5_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_16px_34px_rgba(15,23,42,0.08)] border border-slate-900/10 rounded-[1.6rem] w-20 sm:w-24 h-20 sm:h-24"
                        whileHover={{ 
                          scale: 1.08, 
                          rotate: 5,
                          backgroundColor: "#22c55e"
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ 
                            scale: 1.1,
                            color: "#ffffff"
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <span className="[&>svg]:w-9 [&>svg]:h-9 sm:[&>svg]:w-11 sm:[&>svg]:h-11 [&>svg]:stroke-[2.4]">
                            {benefit.icon}
                          </span>
                        </motion.div>
                      </motion.div>
                    </div>

                    <div className="mt-10 text-center">
                      <motion.h3 
                        className="mb-5 font-semibold text-slate-950 text-2xl sm:text-3xl leading-tight"
                        whileHover={{ x: 2 }}
                        transition={{ duration: 0.2 }}
                      >
                        {benefit.title}
                      </motion.h3>
                      
                      <motion.p 
                        className="text-slate-700 text-base sm:text-lg leading-8"
                        initial={{ opacity: 0.82 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {benefit.description}
                      </motion.p>
                    </div>

                    <div className="flex items-center gap-2 mt-8 font-semibold text-green-700 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span>Mehr Sicherheit</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Machine Section - Mobile optimiert */}
      <section id="maschine" className="relative bg-white py-20 sm:py-28">
        <div className="absolute inset-0 bg-white"></div>
        
        <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <motion.div 
            className="mb-12 sm:mb-16 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeIn()}
              className="mb-4 sm:mb-6 font-semibold text-slate-950 text-3xl sm:text-4xl md:text-6xl tracking-normal"
            >
              {machineTitleParts[0]}<span className="text-green-600">Octoplus 21</span>{machineTitleParts[1]}
            </motion.h2>
            <motion.p 
              variants={fadeIn(0.1)}
              className="mx-auto px-2 max-w-2xl text-slate-500 text-base sm:text-lg leading-8"
            >
              {getContent('machine', 'section_title', 'content', 'Die perfekte Lösung für alle Ihre Höhenarbeiten – modern, sicher und zuverlässig.')}
            </motion.p>
          </motion.div>

          <motion.div 
            className="mx-auto max-w-5xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeIn()}
            whileHover={{ y: -4, scale: 1.01 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <Card className="group relative bg-white shadow-[0_28px_90px_rgba(15,23,42,0.10)] hover:shadow-[0_36px_110px_rgba(15,23,42,0.14)] border border-slate-200/70 rounded-2xl overflow-hidden transition-all duration-500">
              {/* Premium Corner Decorations */}
              <div className="top-0 left-0 absolute bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 rounded-br-[3rem] w-24 h-24 transition-all duration-700"></div>
              <div className="right-0 bottom-0 absolute bg-gradient-to-tl from-emerald-500/10 via-green-500/5 to-transparent opacity-0 group-hover:opacity-100 rounded-tl-[3rem] w-24 h-24 transition-all duration-700"></div>
              
              <div className="relative">
                <div className="top-4 right-4 z-10 absolute">
                  <motion.span 
                    className="bg-emerald-500 shadow-lg hover:shadow-xl px-4 py-2 rounded-full font-semibold text-white text-sm transition-all duration-300"
                    whileHover={{ scale: 1.05, y: -2 }}
                    animate={{
                      boxShadow: [
                        "0 4px 6px rgba(16, 185, 129, 0.22)",
                        "0 8px 18px rgba(16, 185, 129, 0.32)",
                        "0 4px 6px rgba(16, 185, 129, 0.22)"
                      ]
                    }}
                    transition={{
                      boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                    }}
                  >
                    Verfügbar
                  </motion.span>
                </div>
                
                <div className="relative flex justify-center items-center bg-[#f2f4f7] p-6 sm:p-10 h-72 sm:h-[430px] overflow-hidden">
                  <picture className="contents">
                    <source srcSet="/images/platforms/octoplus-21.webp" type="image/webp" />
                    <img
                      src={getImageUrl(platformData?.image_url || getContent('machine', 'main_image', 'image_url', '/images/platforms/octoplus-21.png'))}
                      alt="Octoplus 21 Gelenkteleskopbühne"
                      width="547"
                      height="339"
                      loading="lazy"
                      decoding="async"
                      sizes="(min-width: 768px) 640px, 100vw"
                      className="drop-shadow-[0_24px_45px_rgba(15,23,42,0.16)] w-full h-full object-contain"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none"></div>
                </div>
                
                <CardContent className="p-4 sm:p-8">
                  <div className="gap-6 sm:gap-8 grid grid-cols-1 md:grid-cols-2">
                    <div>
                      <h3 className="mb-4 font-semibold text-slate-950 text-2xl sm:text-4xl">{platformData?.model_name || 'Octoplus 21'}</h3>
                      <p className="mb-4 sm:mb-6 font-semibold text-green-600 text-base sm:text-lg">{platformData?.platform_type || 'Gelenkteleskopbühne'}</p>
                      
                      <div className="space-y-3 sm:space-y-4 text-slate-600">
                        <p className="flex items-center text-sm sm:text-base">
                          <ArrowUp className="mr-3 w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                          <span className="font-medium">{platformData?.working_height || 21}m Arbeitshöhe</span>
                        </p>
                        <p className="flex items-center text-sm sm:text-base">
                          <ArrowRight className="mr-3 w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                          <span className="font-medium">{platformData?.lateral_reach || 18}m seitliche Reichweite</span>
                        </p>
                        <p className="flex items-center text-sm sm:text-base">
                          <Wrench className="mr-3 w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                          <span className="font-medium">{platformData?.drive_type || 'Raupenfahrwerk'} für jeden Untergrund</span>
                        </p>
                        <p className="flex items-center text-sm sm:text-base">
                          <ShieldCheck className="mr-3 w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                          <span className="font-medium">Maximale Sicherheit nach CE-Norm</span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col bg-[#f3faf5] p-4 sm:p-6 rounded-2xl">
                      <h4 className="mb-4 font-semibold text-slate-950 text-lg sm:text-xl">Ideal für:</h4>
                      <ul className="flex-grow space-y-2 text-slate-600 text-sm sm:text-base">
                        <li>• Baumschnitt und Gartenbau</li>
                        <li>• Fassadenreinigung</li>
                        <li>• Montagearbeiten</li>
                        <li>• Dacharbeiten</li>
                        <li>• Malerarbeiten</li>
                        <li>• Veranstaltungstechnik</li>
                      </ul>
                      
                      <div className="bg-white mb-4 sm:mb-6 p-3 sm:p-4 rounded-2xl shadow-sm">
                        <p className="mb-2 font-bold text-green-600 text-xl sm:text-2xl">ab €{platformData?.daily_rate || 150}/Tag</p>
                        <p className="text-slate-500 text-xs sm:text-sm">inkl. Einweisung</p>
                      </div>

                      <div className="space-y-3">
                        <Link to={createPageUrl('Booking')} className="block">
                          <Button 
                            size="lg" 
                            className="group bg-slate-950 hover:bg-slate-800 shadow-lg py-3 sm:py-4 rounded-full w-full text-white text-sm sm:text-base transition-all hover:-translate-y-1 duration-300 transform"
                          >
                            Jetzt anfragen
                            <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                          </Button>
                        </Link>
                        {((platformData?.datasheet_pdf && platformData.datasheet_pdf.trim() !== '') || datasheetUrl) && (
                          <a href={platformData?.datasheet_pdf || datasheetUrl} target="_blank" rel="noopener noreferrer" download>
                            <Button
                              size="lg"
                              variant="outline"
                              className="hover:bg-slate-50 py-3 border-slate-300 rounded-full w-full text-slate-700 text-sm sm:text-base"
                            >
                              <Download className="mr-2 w-3 sm:w-4 h-3 sm:h-4" />
                              Datenblatt
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Contact Section - Mobile optimiert */}
      <section id="kontakt" className="z-10 relative bg-gradient-to-br from-slate-900 via-green-900 to-emerald-900 pt-24 pb-16 sm:py-24" style={{
        clipPath: 'polygon(0 3%, 100% 0%, 100% 100%, 0% 97%)'
      }}>
        {/* Background SVG Pattern */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat bg-center opacity-10"
          style={{
            backgroundImage: `url('/images/assets/bg.svg')`
          }}
        ></div>
        <div className="z-10 relative mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="items-start gap-8 sm:gap-12 grid grid-cols-1 lg:grid-cols-2"
          >
            {/* Contact Info */}
            <motion.div variants={fadeIn()} className="text-white">
              <h2 className="mb-4 sm:mb-6 font-bold text-2xl sm:text-3xl md:text-4xl">
                {getContent('contact', 'section_title', 'title', 'Bereit für Ihr Projekt?')}
              </h2>
              <p className="mb-6 sm:mb-8 text-slate-300 text-lg sm:text-xl">
                {getContent('contact', 'section_title', 'content', 'Kontaktieren Sie uns für ein unverbindliches Angebot. Wir beraten Sie gerne bei der Planung Ihres Projekts.')}
              </p>
              
              <div className="space-y-4 sm:space-y-6">
                <motion.div 
                  className="group flex items-center space-x-4 hover:bg-white/5 p-3 rounded-xl transition-all duration-300 cursor-pointer"
                  whileHover={{ x: 8, scale: 1.02 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <motion.div 
                    className="flex justify-center items-center bg-green-600/20 group-hover:bg-green-500/30 rounded-xl w-12 sm:w-14 h-12 sm:h-14 transition-all duration-300"
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: [0, -5, 5, 0],
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)"
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Phone className="w-5 sm:w-6 h-5 sm:h-6 text-green-400 group-hover:text-green-300 transition-colors duration-300" />
                  </motion.div>
                  <div>
                    <p className="font-semibold group-hover:text-green-300 text-sm sm:text-base transition-colors duration-300">{getContent('contact', 'phone', 'title', 'Telefon')}</p>
                    <p className="text-slate-300 group-hover:text-white text-sm sm:text-base transition-colors duration-300">{getContent('contact', 'phone', 'content', '+49 (0) 123 456 789')}</p>
                  </div>
                  <motion.div 
                    className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    <ArrowRight className="w-4 h-4 text-green-400" />
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="group flex items-center space-x-4 hover:bg-white/5 p-3 rounded-xl transition-all duration-300 cursor-pointer"
                  whileHover={{ x: 8, scale: 1.02 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <motion.div 
                    className="flex justify-center items-center bg-green-600/20 group-hover:bg-green-500/30 rounded-xl w-12 sm:w-14 h-12 sm:h-14 transition-all duration-300"
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: [0, -5, 5, 0],
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)"
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Mail className="w-5 sm:w-6 h-5 sm:h-6 text-green-400 group-hover:text-green-300 transition-colors duration-300" />
                  </motion.div>
                  <div>
                    <p className="font-semibold group-hover:text-green-300 text-sm sm:text-base transition-colors duration-300">{getContent('contact', 'email', 'title', 'E-Mail')}</p>
                    <p className="text-slate-300 group-hover:text-white text-sm sm:text-base transition-colors duration-300">{publicContactEmail}</p>
                  </div>
                  <motion.div 
                    className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    <ArrowRight className="w-4 h-4 text-green-400" />
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="group flex items-center space-x-4 hover:bg-white/5 p-3 rounded-xl transition-all duration-300 cursor-pointer"
                  whileHover={{ x: 8, scale: 1.02 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <motion.div 
                    className="flex justify-center items-center bg-green-600/20 group-hover:bg-green-500/30 rounded-xl w-12 sm:w-14 h-12 sm:h-14 transition-all duration-300"
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: [0, -5, 5, 0],
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)"
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <MapPin className="w-5 sm:w-6 h-5 sm:h-6 text-green-400 group-hover:text-green-300 transition-colors duration-300" />
                  </motion.div>
                  <div>
                    <p className="font-semibold group-hover:text-green-300 text-sm sm:text-base transition-colors duration-300">{getContent('contact', 'service_area', 'title', 'Service-Gebiet')}</p>
                    <p className="text-slate-300 group-hover:text-white text-sm sm:text-base transition-colors duration-300">{getContent('contact', 'service_area', 'content', 'Spreewald und Umgebung')}</p>
                  </div>
                  <motion.div 
                    className="opacity-0 group-hover:opacity-100 ml-auto transition-opacity duration-300"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    <ArrowRight className="w-4 h-4 text-green-400" />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              variants={fadeIn(0.2)}
              whileHover={{ y: -4, scale: 1.01 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card className="group relative bg-white/10 hover:bg-white/15 shadow-2xl hover:shadow-premium backdrop-blur-lg border-white/20 hover:border-white/30 rounded-3xl overflow-hidden transition-all duration-500">
                {/* Premium Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                
                {/* Animated Corner Highlights */}
                <div className="top-0 left-0 absolute bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 rounded-br-3xl w-20 h-20 transition-all duration-500"></div>
                <div className="right-0 bottom-0 absolute bg-gradient-to-tl from-white/10 to-transparent opacity-0 group-hover:opacity-100 rounded-tl-3xl w-20 h-20 transition-all duration-500"></div>
                
                <div className="z-10 relative p-4 pt-8 sm:p-8">
                  <motion.h3 
                    className="mb-4 sm:mb-6 font-bold text-white group-hover:text-green-300 text-xl sm:text-2xl transition-colors duration-300"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    ✨ Anfrage senden
                  </motion.h3>
                  
                  <form onSubmit={handleContactSubmit} className="space-y-4 sm:space-y-6">
                    <input
                      type="text"
                      name="website"
                      value={contactForm.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="hidden"
                      tabIndex="-1"
                      autoComplete="off"
                      aria-hidden="true"
                    />
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Input
                        placeholder="Ihr Name *"
                        value={contactForm.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        className="bg-white/10 py-3 border-white/20 rounded-xl text-white placeholder:text-slate-300"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Input
                        type="email"
                        placeholder="Ihre E-Mail-Adresse *"
                        value={contactForm.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="bg-white/10 py-3 border-white/20 rounded-xl text-white placeholder:text-slate-300"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Input
                        type="tel"
                        placeholder="Ihre Telefonnummer"
                        value={contactForm.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="bg-white/10 py-3 border-white/20 rounded-xl text-white placeholder:text-slate-300"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Textarea
                        placeholder="Beschreiben Sie Ihr Projekt... (Zeitraum, Einsatzort, besondere Anforderungen)"
                        value={contactForm.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        rows={4}
                        className="bg-white/10 border-white/20 rounded-xl text-white placeholder:text-slate-300"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-gradient-to-r from-green-600 hover:from-green-700 to-emerald-600 hover:to-emerald-700 shadow-xl hover:shadow-2xl py-3 sm:py-4 rounded-xl w-full font-semibold text-white text-sm sm:text-base transition-all duration-300"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="mr-2 border-white border-b-2 rounded-full w-4 sm:w-5 h-4 sm:h-5 animate-spin"></div>
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            Anfrage senden
                            <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </form>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Partner Logo Carousel */}
      <section className="relative bg-white py-6 sm:py-8 overflow-hidden" aria-label="Partner">
        <div className="mb-4 sm:mb-5 text-center">
          <p className="font-semibold text-slate-500 text-[11px] sm:text-xs uppercase tracking-[0.24em]">Partner</p>
        </div>

        <div
          className="relative overflow-hidden py-1"
          style={{
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
            maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)'
          }}
        >
          <motion.div
            className="flex gap-4 sm:gap-6 w-max will-change-transform"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 34, repeat: Infinity, ease: 'linear' }}
          >
            {[...Array(2)].flatMap((_, repeatIndex) => partners.map((partner) => (
              <a
                key={`${partner.name}-${repeatIndex}`}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-shrink-0 justify-center items-center bg-white hover:bg-slate-50 shadow-sm hover:shadow-lg p-4 border border-slate-200/80 rounded-xl w-56 sm:w-64 md:w-72 h-20 sm:h-24 transition-all duration-300"
                aria-label={`${partner.name} öffnen`}
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  width="190"
                  height="48"
                  className="max-w-[150px] sm:max-w-[175px] md:max-w-[190px] max-h-10 sm:max-h-12 object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
                <span className="hidden font-semibold text-slate-800 text-base sm:text-lg">{partner.name}</span>
              </a>
            )))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-slate-900 pt-10">
        <div className="relative bg-gradient-to-b from-slate-900 to-slate-950">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-7xl">
            <div className="gap-6 sm:gap-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
              <div className="space-y-4 col-span-1 sm:col-span-2 md:col-span-1">
                <div className="flex items-center space-x-3">
                  <div className="flex justify-center items-center bg-gradient-to-br from-green-600 to-emerald-600 shadow-lg p-1 rounded-lg w-8 h-8">
                    {getContent('footer', 'footer_logo', 'image_url') ? (
                      <img
                        src={getImageUrl(getContent('footer', 'footer_logo', 'image_url'))}
                        alt="Logo"
                        width="32"
                        height="32"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Construction className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <h3 className="font-bold text-white text-xl">{getContent('hero', 'main_title', 'title', 'Spreewald Lift')}</h3>
                </div>
                <p className="max-w-sm text-slate-400 text-sm leading-relaxed">
                  {getContent('footer', 'company_description', 'content', 'Ihr zuverlässiger Partner für Arbeitsbühnen-Vermietung im Spreewald und Umgebung.')}
                </p>
                {isBitvEnhanced && (
                  <button
                    type="button"
                    onClick={() => setBitvWidgetOpen(true)}
                    className="md:hidden inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2.5 rounded-full font-semibold text-white text-sm transition-colors"
                    aria-label="Barrierefreiheit Einstellungen öffnen"
                  >
                    <Accessibility className="w-4 h-4" aria-hidden="true" />
                    Barrierefreiheit
                  </button>
                )}
              </div>
              
              <div>
                <h4 className="mb-4 font-semibold text-white text-sm">{getContent('footer', 'services_title', 'title', 'Services')}</h4>
                <ul className="space-y-3 text-sm">
                  <li><Link to="/arbeitsbuehnen-mieten" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Arbeitsbühnen mieten</Link></li>
                  <li><Link to="/lieferung-aufbau" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Lieferung & Aufbau</Link></li>
                  <li><Link to="/einweisung" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Einweisung</Link></li>
                  <li><Link to="/support-24-7" className="text-slate-400 hover:text-green-400 transition-colors duration-200">24/7 Support</Link></li>
                  <li><Link to="/blog" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Blog</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="mb-4 font-semibold text-white text-sm">{getContent('footer', 'service_areas_title', 'title', 'Service-Gebiet')}</h4>
                <ul className="space-y-3 text-sm">
                  <li><a href="https://www.google.com/maps/search/Spreewald" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Spreewald</a></li>
                  <li><a href="https://www.google.com/maps/search/Cottbus" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Cottbus</a></li>
                  <li><a href="https://www.google.com/maps/search/L%C3%BCbbenau" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Lübbenau</a></li>
                  <li><a href="https://www.google.com/maps/search/L%C3%BCbben" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Lübben</a></li>
                  <li><a href="https://www.google.com/maps/search/Berlin" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors duration-200">Berlin</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="mb-4 font-semibold text-white text-sm">{getContent('footer', 'legal_title', 'title', 'Rechtliches')}</h4>
                <ul className="space-y-3 text-sm">
                  <li><Link to={createPageUrl('Impressum')} className="text-slate-400 hover:text-green-400 transition-colors duration-200">Impressum</Link></li>
                  <li><Link to={createPageUrl('Datenschutz')} className="text-slate-400 hover:text-green-400 transition-colors duration-200">Datenschutz</Link></li>
                  <li><Link to={createPageUrl('Booking')} className="text-slate-400 hover:text-green-400 transition-colors duration-200">Anfrage</Link></li>
                </ul>
              </div>
            </div>
            
            <div className="mt-12 pt-8 border-slate-800 border-t">
              <div className="flex sm:flex-row flex-col justify-between items-center space-y-4 sm:space-y-0">
                <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} {getContent('footer', 'copyright', 'content', 'Spreewald Lift. Alle Rechte vorbehalten.')}</p>
                <div className="flex items-center space-x-1 text-slate-500 text-xs">
                  <span>Powered by</span>
                  <a href="https://pixelcoda.de" target="_blank" rel="noopener noreferrer" className="font-medium text-green-400 hover:text-green-300 transition-colors">
                    pixelcoda
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* BITV Accessibility Widget */}
      <AnimatePresence>
        {isBitvEnhanced && bitvWidgetOpen && (
          <motion.div
            className="z-[90] fixed inset-0 bg-slate-950/35 backdrop-blur-sm p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="presentation"
            onClick={() => setBitvWidgetOpen(false)}
          >
            <motion.section
              className="ml-auto bg-[#f7f8f5] shadow-2xl border border-white/80 rounded-[1.75rem] sm:rounded-[2rem] w-full max-w-3xl max-h-[92vh] overflow-hidden text-slate-950"
              initial={{ x: 80, opacity: 0, scale: 0.98 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              exit={{ x: 80, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="bitv-widget-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center gap-3 bg-gradient-to-r from-green-700 to-slate-950 px-5 sm:px-8 py-5 sm:py-6 text-white">
                <span className="flex flex-shrink-0 justify-center items-center bg-white rounded-full w-12 sm:w-14 h-12 sm:h-14 text-green-700">
                  <Accessibility className="w-7 h-7" aria-hidden="true" />
                </span>
                <h2 id="bitv-widget-title" className="flex-1 font-bold text-3xl sm:text-5xl leading-tight">
                  Barrierefreiheit
                </h2>
                <button
                  type="button"
                  onClick={resetBitvOptions}
                  className="flex justify-center items-center bg-white/10 hover:bg-white/20 rounded-full w-11 h-11 transition-colors"
                  aria-label="Barrierefreiheit Einstellungen zurücksetzen"
                >
                  <RotateCcw className="w-5 h-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setBitvWidgetOpen(false)}
                  className="flex justify-center items-center bg-white/10 hover:bg-white/20 rounded-full w-11 h-11 transition-colors"
                  aria-label="Barrierefreiheit Fenster schließen"
                >
                  <X className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              <div className="space-y-5 sm:space-y-6 p-4 sm:p-8 max-h-[calc(92vh-6.5rem)] overflow-y-auto">
                <section className="bg-white p-4 sm:p-6 rounded-[1.5rem] sm:rounded-[1.75rem]">
                  <h3 className="mb-4 sm:mb-5 font-bold text-slate-700 text-2xl sm:text-3xl">Text</h3>
                  <div className="gap-3 sm:gap-4 grid grid-cols-1 sm:grid-cols-2">
                    <BitvOptionButton
                      active={bitvOptions.largeText}
                      icon={Type}
                      label="Größerer Text"
                      onClick={() => toggleBitvOption('largeText')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.lineHeight}
                      icon={AlignLeft}
                      label="Zeilenhöhe"
                      onClick={() => toggleBitvOption('lineHeight')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.textAlign}
                      icon={AlignLeft}
                      label="Textausrichtung"
                      onClick={() => toggleBitvOption('textAlign')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.readableFont}
                      icon={Baseline}
                      label="Lesbare Schrift"
                      onClick={() => toggleBitvOption('readableFont')}
                    />
                  </div>
                </section>

                <section className="bg-white p-4 sm:p-6 rounded-[1.5rem] sm:rounded-[1.75rem]">
                  <h3 className="mb-4 sm:mb-5 font-bold text-slate-700 text-2xl sm:text-3xl">Visuell</h3>
                  <div className="gap-3 sm:gap-4 grid grid-cols-1 sm:grid-cols-2">
                    <BitvOptionButton
                      active={bitvOptions.highContrast}
                      icon={Contrast}
                      label="Kontrast"
                      onClick={() => toggleBitvOption('highContrast')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.grayscale}
                      icon={Contrast}
                      label="Graustufen"
                      onClick={() => toggleBitvOption('grayscale')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.hideImages}
                      icon={ImageOff}
                      label="Bilder ausblenden"
                      onClick={() => toggleBitvOption('hideImages')}
                    />
                    <BitvOptionButton
                      active={bitvOptions.pauseAnimations}
                      icon={Pause}
                      label="Animationen anhalten"
                      onClick={() => toggleBitvOption('pauseAnimations')}
                    />
                  </div>
                </section>
              </div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>

      {isBitvEnhanced && !bitvWidgetOpen && !mobileMenuOpen && (
        <button
          type="button"
          onClick={() => setBitvWidgetOpen(true)}
          className="hidden md:inline-flex left-6 bottom-6 z-40 fixed justify-center items-center bg-green-600 hover:bg-green-700 shadow-lg rounded-full w-14 h-14 text-white transition-colors"
          aria-label="Barrierefreiheit Einstellungen öffnen"
        >
          <Accessibility className="w-6 h-6" aria-hidden="true" />
        </button>
      )}

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && !mobileMenuOpen && (
          <motion.div
            className="right-4 sm:right-6 bottom-4 sm:bottom-6 z-40 fixed"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <Button
              onClick={scrollToTop}
              size="icon"
              aria-label="Nach oben scrollen"
              className="bg-green-600 hover:bg-green-700 shadow-lg rounded-full w-11 sm:w-14 h-11 sm:h-14 text-white"
            >
              <ArrowUp className="w-5 sm:w-6 h-5 sm:h-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
